import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useSquiggle } from './useSquiggle';
import GameLobby from '../shared/GameLobby';
import { Info } from 'lucide-react';

const SQUIGGLE_OPTIONS = [
  { label: '10s', value: 10 },
  { label: '15s', value: 15 },
];

const TRANSFORM_OPTIONS = [
  { label: '30s', value: 30 },
  { label: '60s', value: 60 },
  { label: '90s', value: 90 },
];

export default function SquiggleLobby() {
  const { state } = useApp();
  const { game, user } = state;
  const { initSquiggle, startSquiggle, endGame } = useSquiggle();

  const [squiggleDuration, setSquiggleDuration] = useState(10);
  const [transformDuration, setTransformDuration] = useState(60);
  const [enableVoting, setEnableVoting] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const memberCount = Object.keys(state.room.members || {}).length;
  const pairCount = Math.ceil(memberCount / 2);

  const handleInit = () => {
    initSquiggle({ squiggleDuration, transformDuration, enableVoting });
    setInitialised(true);
  };

  return (
    <GameLobby
      title="Squiggle Game"
      emoji="〰️"
      onStart={initialised ? startSquiggle : handleInit}
      onCancel={endGame}
      startLabel={initialised ? 'Start Squiggling!' : 'Set Up'}
      startDisabled={false}
    >
      {user.isHost ? (
        <div className="space-y-4">
          {/* Squiggle duration */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Squiggle Time
            </p>
            <div className="flex gap-2">
              {SQUIGGLE_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setSquiggleDuration(value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    squiggleDuration === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Transform duration */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Transform Time
            </p>
            <div className="flex gap-2">
              {TRANSFORM_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => setTransformDuration(value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-display transition-all ${
                    transformDuration === value
                      ? 'bg-amber text-white font-semibold'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Pair count info */}
          <div className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
            <p className="text-sm text-charcoal font-display">
              <span className="font-semibold">{pairCount}</span> pair{pairCount !== 1 ? 's' : ''} will take turns
            </p>
            <p className="text-xs text-slate-400 font-display mt-0.5">
              One squiggles, the other transforms it into something recognisable
            </p>
          </div>

          {/* Enable voting toggle */}
          <button
            onClick={() => setEnableVoting(!enableVoting)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
              enableVoting
                ? 'border-amber/30 bg-amber/5'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <span className="text-lg">{enableVoting ? '🗳️' : '🖼️'}</span>
            <div className="text-left">
              <p className="text-sm font-medium text-charcoal font-display">
                Voting {enableVoting ? '(On)' : '(Off)'}
              </p>
              <p className="text-xs text-slate-400">
                {enableVoting
                  ? 'Players vote on the best transformation after the gallery'
                  : 'Gallery only — no competitive voting'}
              </p>
            </div>
          </button>

          {/* Historical blurb */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center gap-2 text-xs text-slate-400 font-display hover:text-slate-500 transition-colors"
          >
            <Info className="w-3.5 h-3.5" />
            <span>About the Squiggle Game</span>
          </button>
          {showHistory && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
              <p className="text-xs text-violet-600 font-display leading-relaxed">
                Invented by child psychotherapist D.W. Winnicott in the 1960s.
                One person draws a random squiggle, the other turns it into
                something recognisable. Winnicott called the shared creative zone
                between two people &lsquo;potential space&rsquo;.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {game.pairs && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
              <p className="text-sm text-charcoal font-display">
                <span className="font-semibold">{game.pairs.length}</span> pair{game.pairs.length !== 1 ? 's' : ''} ready to go
              </p>
            </div>
          )}
          <p className="text-sm text-slate-400 font-display text-center">
            Waiting for host to start...
          </p>
        </div>
      )}
    </GameLobby>
  );
}
